import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFailQaWithDefectsMutation, useGetQaSummaryQuery, useListCrewsQuery } from '@/redux/internal-services/jobsApi';
import { Button } from '@/components/UI/Button';

interface Props {
  engagementId: string;
  propertyAddress?: string;
  /** Pre-select "fail" when opened via Fail QA button. */
  initialResult?: 'pass' | 'fail';
  onPass: (notes?: string) => void;
  onFail: () => void;
  onCancel: () => void;
}

const QaInspectModal: React.FC<Props> = ({
  engagementId, propertyAddress, initialResult, onPass, onFail, onCancel,
}) => {
  const { t } = useTranslation();
  const [failQa, { isLoading: submittingFail }] = useFailQaWithDefectsMutation();
  const { data: crewList } = useListCrewsQuery();
  const { data: existingQa } = useGetQaSummaryQuery(engagementId, { skip: !engagementId });
  const [result, setResult] = useState<'pass' | 'fail' | null>(initialResult ?? null);
  const [qaNotes, setQaNotes] = useState('');
  const [issueInput, setIssueInput] = useState('');
  const [issues, setIssues] = useState<string[]>([]);
  const [customerObservations, setCustomerObservations] = useState('');
  const [correctiveWork, setCorrectiveWork] = useState('');
  const [responsibleCrewId, setResponsibleCrewId] = useState('');
  const [taskInput, setTaskInput] = useState('');
  const [reworkTasks, setReworkTasks] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [submittingPass, setSubmittingPass] = useState(false);

  const hasHistory = existingQa && existingQa.qa_result;
  const roundNumber = hasHistory ? 2 : 1;

  const addIssue = () => {
    const v = issueInput.trim();
    if (v) { setIssues((p) => [...p, v]); setIssueInput(''); }
  };
  const removeIssue = (i: number) => setIssues((p) => p.filter((_, idx) => idx !== i));

  const addTask = () => {
    const v = taskInput.trim();
    if (v) { setReworkTasks((p) => [...p, v]); setTaskInput(''); }
  };
  const removeTask = (i: number) => setReworkTasks((p) => p.filter((_, idx) => idx !== i));

  const canSubmit = result !== null && (result === 'pass' || (result === 'fail' && correctiveWork.trim().length > 0));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!result) { setError(t('jobs.qaResultRequired', 'Select a QA result first.')); return; }
    if (result === 'pass') {
      setSubmittingPass(true);
      try { onPass(qaNotes.trim() || undefined); } finally { setSubmittingPass(false); }
      return;
    }
    if (!correctiveWork.trim()) {
      setError(t('jobs.qaFailReasonRequired', 'Describe the corrective work required.'));
      return;
    }
    try {
      await failQa({
        engagementId,
        reason: correctiveWork.trim(),
        defect_items: reworkTasks
          .map((d) => d.trim())
          .filter(Boolean)
          .map((description) => ({ description })),
      }).unwrap();
      onFail();
    } catch (err) {
      setError(
        (err as { data?: { message?: string } })?.data?.message
          || t('jobs.qaFailFailed', 'Could not record the QA result.'),
      );
    }
  };

  const isBusy = submittingPass || submittingFail;

  const formatDate = (iso?: string | null) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 overflow-y-auto"
      onClick={onCancel}
    >
      <div
        className="my-4 mx-4 w-full max-w-[560px] rounded-2xl bg-white shadow-2xl overflow-hidden dark:bg-ui-card"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── header ── */}
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-ui-border">
          <div className="flex items-center gap-2 min-w-0">
            <span className="shrink-0 rounded bg-brand-dark px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-widest text-white">
              {t('jobs.qaLabel', 'QA')}
            </span>
            <p className="truncate text-[13px] font-semibold text-ui-text">
              {t('jobs.qaModalTitle', 'Record QA result')}
              {propertyAddress && (
                <span className="ml-1 font-normal text-ui-text3"> · {propertyAddress}</span>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="shrink-0 flex h-6 w-6 items-center justify-center rounded text-ui-text3 hover:bg-ui-hover hover:text-ui-text text-lg leading-none"
            aria-label={t('common.close', 'Close')}
          >
            ×
          </button>
        </div>

        <form onSubmit={(e) => void handleSubmit(e)} className="divide-y divide-ui-border">
          {/* ── result ── */}
          <div className="px-4 py-3 space-y-2">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-ui-text3">
              {t('jobs.qaResult', 'Result')}
            </p>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setResult('pass')}
                className={`rounded-xl border px-4 py-3 text-left transition-all ${
                  result === 'pass'
                    ? 'border-green-500 bg-green-50 dark:border-green-600/60 dark:bg-green-900/20'
                    : 'border-ui-border bg-ui-subtle hover:border-green-400'
                }`}
              >
                <p className={`flex items-center gap-1 text-[13px] font-semibold ${result === 'pass' ? 'text-green-700 dark:text-green-300' : 'text-ui-text'}`}>
                  {result === 'pass' && <span>✓</span>}
                  {t('jobs.qaPass', 'Passed')}
                </p>
                <p className="mt-0.5 text-[11px] text-ui-text3">
                  {t('jobs.qaPassHint', 'Moves to QA Inspected and opens billing tasks.')}
                </p>
              </button>
              <button
                type="button"
                onClick={() => setResult('fail')}
                className={`rounded-xl border px-4 py-3 text-left transition-all ${
                  result === 'fail'
                    ? 'border-red-400 bg-red-50 dark:border-red-600/60 dark:bg-red-900/20'
                    : 'border-ui-border bg-ui-subtle hover:border-red-400'
                }`}
              >
                <p className={`flex items-center gap-1 text-[13px] font-semibold ${result === 'fail' ? 'text-red-700 dark:text-red-300' : 'text-ui-text'}`}>
                  {result === 'fail' && <span>✕</span>}
                  {t('jobs.qaFail', 'Rework required')}
                </p>
                <p className="mt-0.5 text-[11px] text-ui-text3">
                  {t('jobs.qaFailHint', 'Returns the job to Job Execution so the crew can rework it.')}
                </p>
              </button>
            </div>
          </div>

          {/* ── QA notes ── */}
          <div className="px-4 py-3 space-y-1.5">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-ui-text3">
              {t('jobs.qaNotes', 'QA Notes')}
            </p>
            <textarea
              value={qaNotes}
              onChange={(e) => setQaNotes(e.target.value)}
              rows={3}
              placeholder={t('jobs.qaNotesPlaceholder', 'What was inspected and what you found')}
              className="w-full resize-none rounded-lg border border-ui-border bg-ui-input px-3 py-2 text-sm text-ui-text placeholder:text-ui-text3 focus:border-brand-dark focus:outline-none"
            />
          </div>

          {/* ── identified issues ── */}
          <div className="px-4 py-3 space-y-2">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-ui-text3">
              {t('jobs.identifiedIssues', 'Identified Issues')}
            </p>
            <div className="flex gap-2">
              <input
                value={issueInput}
                onChange={(e) => setIssueInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addIssue(); } }}
                placeholder={t('jobs.identifiedIssuePlaceholder', 'Describe an issue and the rework needed')}
                className="flex-1 rounded-lg border border-ui-border bg-ui-input px-3 py-2 text-sm text-ui-text placeholder:text-ui-text3 focus:border-brand-dark focus:outline-none"
              />
              <button
                type="button"
                onClick={addIssue}
                className="rounded-lg border border-ui-border bg-ui-subtle px-4 py-2 text-sm font-medium text-ui-text hover:bg-ui-hover"
              >
                {t('common.add', 'Add')}
              </button>
            </div>
            {issues.length > 0 && (
              <ul className="space-y-1">
                {issues.map((iss, i) => (
                  <li key={i} className="flex items-start gap-2 rounded-lg bg-ui-subtle px-3 py-1.5 text-sm text-ui-text">
                    <span className="mt-0.5 text-ui-text3">·</span>
                    <span className="flex-1">{iss}</span>
                    <button type="button" onClick={() => removeIssue(i)} className="text-ui-text3 hover:text-red-500 text-[11px]">✕</button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* ── customer observations ── */}
          <div className="px-4 py-3 space-y-1.5">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-ui-text3">
              {t('jobs.customerObservations', 'Customer Observations')}
            </p>
            <textarea
              value={customerObservations}
              onChange={(e) => setCustomerObservations(e.target.value)}
              rows={2}
              placeholder={t('jobs.customerObservationsPlaceholder', 'Anything the customer raised at walkthrough')}
              className="w-full resize-none rounded-lg border border-ui-border bg-ui-input px-3 py-2 text-sm text-ui-text placeholder:text-ui-text3 focus:border-brand-dark focus:outline-none"
            />
          </div>

          {/* ── photos & documents ── */}
          <div className="px-4 py-3 space-y-2">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-ui-text3">
              {t('jobs.photosDocuments', 'Photos & Documents')}
            </p>
            <div className="flex gap-2">
              <button type="button" className="flex items-center gap-1.5 rounded-lg border border-ui-border bg-ui-subtle px-3 py-2 text-sm text-ui-text2 hover:bg-ui-hover">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/>
                </svg>
                {t('jobs.addPhotos', 'Add photos')}
              </button>
              <button type="button" className="flex items-center gap-1.5 rounded-lg border border-ui-border bg-ui-subtle px-3 py-2 text-sm text-ui-text2 hover:bg-ui-hover">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/>
                </svg>
                {t('jobs.addDocuments', 'Add documents')}
              </button>
            </div>
          </div>

          {/* ── corrective work (fail only) ── */}
          {result === 'fail' && (
            <div className="px-4 py-3 space-y-3 bg-red-50/60 dark:bg-red-900/10">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-red-700 dark:text-red-400">
                {t('jobs.correctiveWork', 'Corrective Work — Required')}
              </p>
              <textarea
                value={correctiveWork}
                onChange={(e) => setCorrectiveWork(e.target.value)}
                rows={3}
                placeholder={t('jobs.correctiveWorkPlaceholder', 'Describe the corrective work to be carried out')}
                className="w-full resize-none rounded-lg border border-red-200 bg-white px-3 py-2 text-sm text-ui-text placeholder:text-ui-text3 focus:border-red-500 focus:outline-none dark:bg-ui-input dark:border-red-800/40"
              />
              <div className="space-y-1.5">
                <p className="text-[10px] font-medium text-red-600 dark:text-red-400">
                  {t('jobs.responsibleCrewUser', 'Responsible crew or user')}
                </p>
                <select
                  value={responsibleCrewId}
                  onChange={(e) => setResponsibleCrewId(e.target.value)}
                  className="w-full rounded-lg border border-red-200 bg-white px-3 py-2 text-sm text-ui-text focus:border-red-500 focus:outline-none dark:bg-ui-input dark:border-red-800/40"
                >
                  <option value="">{t('jobs.selectCrewUser', 'Select who carries out the rework')}</option>
                  {(crewList ?? []).map((c) => (
                    <option key={c.crew_id} value={c.crew_id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <p className="text-[10px] font-medium text-red-600 dark:text-red-400">
                  {t('jobs.reworkTasks', 'Rework tasks linked to this job')}
                </p>
                <div className="flex gap-2">
                  <input
                    value={taskInput}
                    onChange={(e) => setTaskInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTask(); } }}
                    placeholder={t('jobs.reworkTaskPlaceholder', 'Task title — assigned to the responsible crew')}
                    className="flex-1 rounded-lg border border-red-200 bg-white px-3 py-2 text-sm text-ui-text placeholder:text-ui-text3 focus:border-red-500 focus:outline-none dark:bg-ui-input dark:border-red-800/40"
                  />
                  <button
                    type="button"
                    onClick={addTask}
                    className="rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 dark:bg-ui-input dark:border-red-800/40 dark:text-red-400"
                  >
                    {t('jobs.addTask', 'Add task')}
                  </button>
                </div>
                {reworkTasks.length > 0 && (
                  <ul className="space-y-1">
                    {reworkTasks.map((task, i) => (
                      <li key={i} className="flex items-center gap-2 rounded-lg bg-white/70 px-3 py-1.5 text-sm text-ui-text dark:bg-ui-subtle">
                        <span className="flex-1">{task}</span>
                        <button type="button" onClick={() => removeTask(i)} className="text-ui-text3 hover:text-red-500 text-[11px]">✕</button>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="text-[10px] text-red-600/80 dark:text-red-400/80 leading-relaxed">
                  {t('jobs.correctiveWorkNote', 'Supporting notes and photos captured above are attached to the rework. The job returns to Job Execution and cannot reach final completion until the corrective work is finished and QA passes.')}
                </p>
              </div>
            </div>
          )}

          {/* ── QA history ── */}
          {hasHistory && (
            <div className="px-4 py-3 space-y-2">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-ui-text3">
                {t('jobs.qaHistory', 'QA History')}
              </p>
              <div className="rounded-xl border border-ui-border bg-ui-subtle p-3 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[11px] font-semibold text-ui-text">
                    {t('jobs.qaRound', 'Round {{n}}', { n: roundNumber - 1 })}
                  </span>
                  <span className={`rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide ${
                    existingQa.qa_result === 'pass'
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                      : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                  }`}>
                    {existingQa.qa_result === 'pass'
                      ? t('jobs.qaPassedBadge', 'Passed')
                      : t('jobs.qaReworkBadge', 'Rework Required')}
                  </span>
                  {existingQa.inspected_by_name && (
                    <span className="text-[11px] text-ui-text2">{existingQa.inspected_by_name}</span>
                  )}
                  {existingQa.inspected_at && (
                    <span className="text-[11px] text-ui-text3">{formatDate(existingQa.inspected_at)}</span>
                  )}
                </div>
                {existingQa.reason && (
                  <p className="text-[12px] text-ui-text leading-relaxed">{existingQa.reason}</p>
                )}
              </div>
            </div>
          )}

          {/* ── error ── */}
          {error && (
            <div className="px-4 pb-3">
              <p className="rounded-lg bg-red-50 px-3 py-2 text-[11px] text-red-600 dark:bg-red-900/20">{error}</p>
            </div>
          )}

          {/* ── footer ── */}
          <div className="px-4 py-3 space-y-3">
            <div className="flex gap-2">
              <Button
                type="submit"
                className="flex-1"
                disabled={isBusy || !canSubmit}
                loading={isBusy}
              >
                {isBusy
                  ? t('jobs.savingQa', 'Saving…')
                  : !canSubmit && result === 'fail'
                    ? t('jobs.correctiveWorkRequired', 'Corrective work and responsible crew required')
                    : t('jobs.recordQaResult', 'Record QA result')}
              </Button>
              <Button type="button" variant="ghost" disabled={isBusy} onClick={onCancel}>
                {t('workflow.cancel', 'Cancel')}
              </Button>
            </div>
            <p className="text-[10px] text-ui-text3 leading-relaxed text-center">
              {t('jobs.qaStampNote', 'The result, notes, issues, observations and evidence are stamped with the inspector and the time, posted to the site board, and kept for every repeated QA cycle.')}
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QaInspectModal;
